import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff, AlertCircle, Shield, CheckCircle } from 'lucide-react';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { autenticacaoService } from '@/services/autenticacaoService';
import { useToast } from '@/hooks/use-toast';

export default function RedefinirSenhaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Pegar email e código dos parâmetros da URL
  const email = searchParams.get('email') || '';
  const codigo = searchParams.get('codigo') || '';
  
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const passwordRules = [
    { label: 'Mínimo 6 caracteres', valid: senha.length >= 6 },
    { label: 'Pelo menos 1 letra maiúscula (A-Z)', valid: /[A-Z]/.test(senha) },
    { label: 'Pelo menos 1 letra minúscula (a-z)', valid: /[a-z]/.test(senha) },
    { label: 'Pelo menos 1 número (0-9)', valid: /\d/.test(senha) },
    { label: 'Pelo menos 1 caractere especial (!@#$...)', valid: /[^A-Za-z0-9]/.test(senha) },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (senha.length < 6) {
      newErrors.senha = 'Senha deve ter pelo menos 6 caracteres';
    } else if (!/[A-Z]/.test(senha) || !/[a-z]/.test(senha) || !/\d/.test(senha) || !/[^A-Za-z0-9]/.test(senha)) {
      newErrors.senha = 'Inclua ao menos 1 maiúscula, 1 minúscula, 1 número e 1 caractere especial';
    }

    if (!confirmarSenha) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (senha !== confirmarSenha) {
      newErrors.confirmarSenha = 'Senhas não coincidem';
    }

    if (!email) {
      newErrors.email = 'E-mail é obrigatório';
    }

    if (!codigo) {
      newErrors.codigo = 'Código é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const setter = field === 'senha' ? setSenha : setConfirmarSenha;
    setter(e.target.value);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Usar o serviço de autenticação para redefinir senha
      const resultado = await autenticacaoService.redefinirSenha({
        id: email,
        codigo: codigo,
        senha: senha,
        confirmacaoSenha: confirmarSenha
      });

      if ('success' in resultado) {
        toast({
          title: 'Senha redefinida com sucesso!',
          description: 'Sua senha foi atualizada. Faça login com sua nova senha.',
        });
        
        // Redirecionar para login após sucesso
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        // Erro na redefinição
        toast({
          title: 'Erro ao redefinir senha',
          description: resultado.failure.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao redefinir senha',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-dark via-primary to-secondary relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-2xl" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-16 object-contain"
            />
          </div>

          {/* Main Content */}
          <div className="space-y-8 max-w-md">
            <div>
              <h2 className="font-heading text-4xl font-bold leading-tight">
                Redefinir sua senha
                <br />
                com segurança
              </h2>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                Crie uma nova senha forte para proteger sua conta. 
                Use letras, números e caracteres especiais.
              </p>
            </div>

            {/* Security Features */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Senha criptografada</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Proteção em tempo real</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white/90">Acesso seguro via HTTPS</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-sm text-white/50">
            <p>© 2024 UrbNet. Todos os direitos reservados.</p>
            <div className="flex gap-4">
              <Link to="/termos-uso" className="hover:text-white/70 transition-colors">Termos</Link>
              <Link to="/politica-privacidade" className="hover:text-white/70 transition-colors">Privacidade</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-14 object-contain"
            />
          </div>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl font-bold">Nova Senha</h2>
            <p className="text-muted-foreground mt-2">
              Digite sua nova senha abaixo
            </p>
          </div>

          {/* Email Display (ReadOnly) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              E-mail
            </Label>
            <Input
              value={email}
              readOnly
              className="h-14 text-base bg-muted/50 border-border/50"
            />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="senha" className="text-sm font-medium">
                Nova Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="senha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={senha}
                  onChange={handleInputChange('senha')}
                  className={cn(
                    'pl-12 pr-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    errors.senha && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.senha && (
                <p className="text-sm text-destructive">{errors.senha}</p>
              )}
              <div className="mt-2 rounded-lg border border-border/60 bg-surface-container p-3 space-y-1 text-xs text-muted-foreground">
                <p className="font-medium text-foreground/80">A senha deve conter:</p>
                <ul className="space-y-1">
                  {passwordRules.map((rule) => (
                    <li key={rule.label} className="flex items-center gap-2">
                      {rule.valid ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className={rule.valid ? 'text-foreground' : ''}>{rule.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmarSenha" className="text-sm font-medium">
                Confirmar Nova Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirmarSenha"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmarSenha}
                  onChange={handleInputChange('confirmarSenha')}
                  className={cn(
                    'pl-12 pr-12 h-14 text-base bg-surface-container border-border/50',
                    'focus:bg-background focus:border-primary transition-colors',
                    errors.confirmarSenha && 'border-destructive'
                  )}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmarSenha && (
                <p className="text-sm text-destructive">{errors.confirmarSenha}</p>
              )}
            </div>

            {/* Error Message */}
            {Object.values(errors).some(error => error) && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive animate-scale-in">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">Verifique os campos destacados</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!senha || !confirmarSenha || isLoading}
              className="w-full h-14 text-base font-medium gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                <>
                  Redefinir Senha
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </Button>
          </form>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Conexão segura com criptografia de ponta a ponta</span>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-primary hover:underline font-medium"
            >
              <ArrowLeft className="inline h-4 w-4 mr-1" />
              Voltar para o login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
